import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { useTranslation } from 'react-i18next';
import { Box, Button, Checkbox, Grid, Paper, TableSortLabel, Tooltip, Typography } from '@mui/material';
import { Dispatch, SetStateAction } from 'react';
import { TableHeader } from '../../../components/forms/TableHeader';
import { TableLoading } from '../../../components/forms/TableLoading';
import { Pill } from '../../../components/shared/Pill';
import { DeliveryScheduleModel, PaginatedList } from '../../../app/services/api/orderManagementClient';
import CustomPagination from '../../../components/forms/Pagination';
import { EmptyRows } from '../../../components/forms/EmptyRows';
import InfoIcon from '@mui/icons-material/Info';

export default function DeliveryScheduleTable (props: {
    readonly list: PaginatedList<DeliveryScheduleModel>;
    readonly page: number,
    readonly setPage: Dispatch<SetStateAction<number>>,
    readonly order: 'asc' | 'desc', 
    readonly setOrder: Dispatch<SetStateAction<'asc' | 'desc'>>,
    readonly orderBy: string,
    readonly setOrderBy: Dispatch<SetStateAction<string>>,
    readonly isLoading: boolean;

    readonly hasWritePermissions: boolean;
    readonly hasDeletePermissions: boolean;
    selectedItems: number[];
    onItemSelectionChange: (itemId: number, isChecked: boolean) => void;
    onSetCutOffDate: (selectedIds: number[]) => void;
    onSetDeliveryDate: (selectedIds: number[]) => void;
    onDelete: (selectedIds: number[]) => void;
    isSelectAll: boolean;
    onSelectAllChange: (isSelectAll: boolean) => void;
}) {
  const { t } = useTranslation('odsmanagement');
  const rowsPerPage = 10;
  const maxPages = props.list?.totalCount
    ? Math.max(1, Math.ceil(props.list.totalCount / rowsPerPage))
    : 0;

  const handleSort = (property: keyof DeliveryScheduleModel) => {
    const isAsc = props.orderBy === property && props.order === 'asc';
    if (props.list) {
      props.setOrder(isAsc ? 'desc' : 'asc');
      props.setOrderBy(property as keyof DeliveryScheduleModel);
    }
  };

  const handleItemChange = (itemId: number, isChecked: boolean) => {
    if (!props.hasWritePermissions) 
    {
      return;
    }

    props.onItemSelectionChange(itemId, isChecked);
  };

  const handleSelectAllChange = (isChecked: boolean) => {
    props.onSelectAllChange(isChecked);
  };

  const handleSetCutOffDate = () => {
    if (!props.hasWritePermissions) 
    {
      return;
    }

    props.onSetCutOffDate(props.selectedItems);
  };

  const handleSetDeliveryDate = () => {
    if (!props.hasWritePermissions) 
    {
      return;
    }

    props.onSetDeliveryDate(props.selectedItems);
  };

  const handleDelete = () => {
    if (!props.hasDeletePermissions) 
    {
      return;
    }

    props.onDelete(props.selectedItems);
  };

  return (
    <>
      <TableHeader>
        <Grid item 
          md={4}>
          <Box>
            {t('deliverySchedules')}
            {props.list?.totalCount > 0 && <Pill text={`${props.list.totalCount} item${props.list.totalCount === 1 ? '' : 's'}`} />}
          </Box>
        </Grid>
        <Grid container 
          md={8}
          spacing={2}
          justifyContent={'flex-end'}> 
          <Grid item>
            <Tooltip title={t('bulkOptionsInfo') || ''}>
              <InfoIcon fontSize='small'
                sx={{ ml: 2, verticalAlign: 'sub' }}/>
            </Tooltip>
          </Grid>
          <Grid item
            xs={12}
            md={2}>
            <Button
              variant="secondary"
              size="md"
              disabled={props.selectedItems.length === 0 || !props.hasWritePermissions}
              fullWidth
              onClick={handleSetCutOffDate}
            >
              {t('setCutOffDate')}
            </Button>
          </Grid>
          <Grid item
            xs={12}
            md={2}>
            <Button
              variant="secondary"
              size="md"
              disabled={props.selectedItems.length === 0 || !props.hasWritePermissions}
              fullWidth
              onClick={handleSetDeliveryDate}
            >
              {t('setDeliveryDate')}
            </Button>
          </Grid>
          <Grid item
            xs={12}
            md={2}>
            <Button
              variant="secondary"
              size="md"
              disabled={props.selectedItems.length === 0 || !props.hasDeletePermissions}
              fullWidth
              onClick={handleDelete}
            >
              {t('delete')}
            </Button>
          </Grid>
        </Grid>
      </TableHeader>
      {props.isLoading ? <TableLoading headerHeight={45}
        bodyRowHeight={40}
        minTableWidth={700} /> :
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {props.hasWritePermissions &&  
                (
                  <TableCell size='small'>
                    <Checkbox
                      color="primary"
                      disabled={!props.hasWritePermissions}
                      checked={props.isSelectAll}
                      onChange={(e) => handleSelectAllChange(e.target.checked)}
                    />
                  </TableCell>
                )}
                <TableCell
                  align="left">
                  <TableSortLabel
                    active={props.orderBy === 'id'}
                    direction={props.order}
                    onClick={() => handleSort('id')}>
                    {t('deliveryScheduleId')}
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  align="left">
                  <TableSortLabel
                    active={props.orderBy === 'storeNumber'}
                    direction={props.order}
                    onClick={() => handleSort('storeNumber')}>
                    {t('storeNumber')}
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  align="left">
                  {t('storeName')}
                </TableCell>
                <TableCell
                  align="left">
                  {t('orderType')}
                </TableCell>
                <TableCell
                  align="left">
                  {t('cutOffTime')}
                </TableCell>
                <TableCell
                  align="left">
                  <TableSortLabel
                    active={props.orderBy === 'cutOffDate'}
                    direction={props.order}
                    onClick={() => handleSort('cutOffDate')}>
                    {t('cutOffDate')}
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  align="left">
                  <TableSortLabel
                    active={props.orderBy === 'deliveryDate'}
                    direction={props.order}
                    onClick={() => handleSort('deliveryDate')}>
                    {t('deliveryDate')}
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  align="left">
                  {t('supplierNumber')}
                </TableCell>
                <TableCell
                  align="left">
                  {t('supplierName')}
                </TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {props.list?.items?.map((row: DeliveryScheduleModel) => {
                const isChecked = props.selectedItems.some(x => x === row.id);
                
                return (
                  <TableRow key={row.id}>
                    {props.hasWritePermissions &&  
                      (
                        <TableCell size="small" 
                          style={{ display: 'flex', alignItems: 'center' }}>
                          <Checkbox
                            color="primary"
                            checked={isChecked}
                            onChange={(e) => handleItemChange(row.id!, e.target.checked)}
                          />
                        </TableCell>
                      )
                    }
                    <TableCell>
                      <Typography variant='textXS'>
                        {row.id ?? ''}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant='textXS' >
                        { row.storeNumber }
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant='textXS' >
                        { row.storeName }
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant='textXS' >
                        { row.orderType }
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant='textXS' >
                        {row.cutOffTime}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant='textXS' >
                        { row.cutOffDate }
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant='textXS' >
                        {row.deliveryDate}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant='textXS' >
                        {row.supplierNumber}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant='textXS' >
                        {row.supplierName}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}

              <EmptyRows numberOfCurrentRows={props.list?.items?.length ?? 0}
                height={40} />
            </TableBody>
          </Table>
        </TableContainer>}
      <CustomPagination useNoDataMessage={false}
        page={props.page}
        setPage={props.setPage}
        maxPages={maxPages}
        breakpointForChangeDisplay={120000}
        disabled={props.isLoading} />
    </>
  );
}